import { Link } from "react-router-dom";
import {
  ArrowRight, Ruler, Activity, Video, Gauge, TrendingUp,
  Target, Trophy, Radio,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

/**
 * WhyArmIQ — the founder's story + the philosophical spine of the platform.
 *
 * The premise: "You can't grow what you don't (or can't) measure."
 * The proof: Rod White's coaching-turnaround record.
 * The bridge: the objective baseline-testing stack ArmIQ was built to
 *   make repeatable — FMS, GIRD, 2D & 3D motion capture, SpeedIQ, and
 *   the four intelligence pillars on top.
 */

const BASELINES = [
  {
    icon: Ruler,
    label: "Functional Movement Screening",
    body:
      "Seven-pattern FMS + GIRD testing captured at baseline and re-screen. The dysfunctional pattern always precedes the injury — we measure so it doesn't stay hidden.",
    color: "#38BDF8",
  },
  {
    icon: Activity,
    label: "MotionIQ · Movement Intelligence",
    body:
      "Range-of-motion mapping, kinetic chain analysis, and asymmetry detection. The starting position for every development plan.",
    color: "#F59E0B",
  },
  {
    icon: Video,
    label: "2D &amp; 3D Motion Capture Overlays",
    body:
      "Side-by-side biomechanical comparison over time. Every athlete's mechanics annotated against their own personal best — not a generic ideal.",
    color: "#D90429",
  },
  {
    icon: Gauge,
    label: "SpeedIQ · Running · Movement · Form",
    body:
      "Velocity, sprint, acceleration, and mechanical form captured with rear-view video and force data. Progress is measurable, not felt.",
    color: "#10B981",
  },
];

const RESULTS = [
  {
    school: "Dillard University",
    body: [
      "Inherited 6–21 midway through the season.",
      "Turnaround: 15–6 the rest of that year (21–27 finish).",
      "Year Two: 26–21 · Invitation to the HBCU World Series.",
      "One of the Top 10 hitting programs in the NAIA.",
      "Documented wins over NCAA D-II opponents · competitive vs D-I.",
      "Pitching staff: individual velocity gains of 4–9 MPH while throwing 62% strikes.",
    ],
    tone: "#D90429",
  },
  {
    school: "Texas College",
    body: [
      "Engineered a 300% improvement in winning percentage.",
      "Transformed an 11–31 (.261) program into 31–32 within one cycle.",
      "Documented victories over NCAA Division I opponents.",
    ],
    tone: "#F59E0B",
  },
  {
    school: "ASA Miami",
    body: [
      "Home runs increased from 2 to 41 in one season — a 1,950% increase.",
      "Team offense: .323 BA · .421 OBP.",
      "25% year-over-year improvement in offensive efficiency.",
    ],
    tone: "#22D3EE",
  },
  {
    school: "Alabama State University",
    body: [
      "Hitting coach for baseball and softball programs.",
      "Whether the jersey changed or the logo was different — the process never did.",
      "Measure. Evaluate. Develop. Repeat.",
    ],
    tone: "#10B981",
  },
];

// ── Nav (compact) ─────────────────────────────────────────────────────
function Nav() {
  return (
    <header className="absolute top-0 left-0 right-0 z-30">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <Logo size="md" variant="lockup" linkTo="/" dark />
        <nav className="hidden md:flex items-center gap-7 text-[13px] text-white/70">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/high-school" className="hover:text-white">High School</Link>
          <Link to="/college" className="hover:text-white">Collegiate</Link>
          <Link to="/professional" className="hover:text-white">Professional</Link>
          <Link to="/pricing" className="hover:text-white">Plans</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-white/80 hover:text-white" data-testid="why-login-link">Sign in</Link>
          <Link to="/signup" data-testid="why-signup-button">
            <Button className="rounded-full px-5 text-white red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request access <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── Body paragraph helper — narrative tone ────────────────────────────
function Para({ children, className = "" }) {
  return (
    <p className={`text-base md:text-lg text-white/80 leading-relaxed ${className}`}>
      {children}
    </p>
  );
}

export default function WhyArmIQ() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="why-page">
      {/* ── HERO ── */}
      <section
        className="relative overflow-hidden pt-40 pb-24 md:pt-52 md:pb-32"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% -10%, rgba(217,4,41,0.25), transparent 55%)," +
            "radial-gradient(ellipse at 90% 10%, rgba(56,189,248,0.10), transparent 55%)," +
            "linear-gradient(180deg, #05090F 0%, #0A1424 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <Nav />
        <div className="relative max-w-4xl mx-auto px-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] tracking-[0.2em] font-bold uppercase"
            style={{ borderColor: "rgba(217,4,41,0.4)", backgroundColor: "rgba(217,4,41,0.10)", color: "var(--brand-secondary)" }}
            data-testid="why-eyebrow"
          >
            <Radio className="h-3 w-3" />
            The Founder&apos;s Premise
          </div>
          <h1
            className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl lg:text-[92px] text-white"
            data-testid="why-headline"
          >
            You can&apos;t grow what<br />
            you can&apos;t{" "}
            <span style={{ color: "var(--brand-secondary)" }}>measure.</span>
          </h1>
          <p className="mt-8 text-xl md:text-2xl text-white/85 font-display font-black tracking-tight leading-snug">
            Why I built ArmIQ.
          </p>
          <p className="mt-4 text-base md:text-lg text-white/60">
            A. &quot;Rod&quot; White · Founder &amp; CEO
          </p>
        </div>
      </section>

      {/* ── NARRATIVE — the coach's story ── */}
      <section
        className="py-20 md:py-28"
        style={{ backgroundColor: "var(--ink-0)" }}
        data-testid="why-narrative"
      >
        <div className="max-w-3xl mx-auto px-6 space-y-8">
          <Para>
            People ask me this question everywhere I go.
          </Para>
          <p className="font-display font-black text-3xl md:text-4xl text-white leading-tight tracking-tight">
            &quot;How were you able to turn programs around so quickly?&quot;
          </p>
          <Para>
            The truth is, it was never about having better players. It was never about one magic drill. It wasn&apos;t one pitching philosophy or one hitting philosophy.
          </Para>
          <Para>
            It was <span className="text-white font-semibold">a system</span>.
          </Para>
          <Para>
            A system built on one simple belief:
          </Para>
          <p className="font-display font-black text-2xl md:text-3xl leading-tight tracking-tight" style={{ color: "var(--brand-secondary)" }}>
            You can&apos;t grow what you can&apos;t measure.
          </p>
          <hr className="border-white/10" />
          <Para>
            At every stop in my career, I stopped asking coaches what they <em>thought</em> was happening and started measuring what was <em>actually</em> happening.
          </Para>
          <Para>
            Not just velocity. Not just exit velocity. Not just batting average. <span className="text-white font-semibold">Everything.</span>
          </Para>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-white/70">
            {["Biomechanics", "Movement quality", "Mobility", "Readiness", "Strength", "Recovery", "Workload", "Performance signals", "Performance trends"].map((k) => (
              <li key={k} className="rounded-lg border border-white/10 bg-white/[0.03] py-2 px-3 text-center">{k}</li>
            ))}
          </ul>
          <Para>
            When you connect all of those pieces together, athletes stop guessing…and start developing with purpose.
          </Para>
        </div>
      </section>

      {/* ── BASELINE MEASUREMENT — the stack ── */}
      <section className="py-20 md:py-28" data-testid="why-baselines">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">
            The measurement stack
          </div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            To support the athlete,<br />
            you first have to{" "}
            <span style={{ color: "var(--brand-secondary)" }}>see them.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-3xl leading-relaxed">
            ArmIQ&apos;s intelligence layer is only as honest as its baselines. That&apos;s why we built the objective measurement stack first — before any dashboards, before any AI, before any recommendations.
          </p>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {BASELINES.map((b) => (
              <div
                key={b.label}
                className="rounded-2xl border p-6"
                style={{ backgroundColor: "rgba(15,30,54,0.55)", borderColor: `${b.color}30` }}
                data-testid={`why-baseline-${b.label.split(" ")[0].toLowerCase()}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${b.color}22`, border: `1px solid ${b.color}55` }}
                >
                  <b.icon className="h-5 w-5" style={{ color: b.color }} />
                </div>
                <h3 className="font-display font-black text-xl text-white tracking-tight" dangerouslySetInnerHTML={{ __html: b.label }} />
                <p className="mt-2 text-[14px] text-white/65 leading-relaxed" dangerouslySetInnerHTML={{ __html: b.body }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESULTS ── */}
      <section
        className="py-20 md:py-28"
        style={{ backgroundColor: "var(--ink-0)" }}
        data-testid="why-results"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">
            The results
          </div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            Different jerseys.<br />
            <span style={{ color: "var(--brand-secondary)" }}>Same process.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {RESULTS.map((r) => (
              <div
                key={r.school}
                className="rounded-2xl border p-6"
                style={{ backgroundColor: "rgba(15,30,54,0.55)", borderColor: `${r.tone}30` }}
                data-testid={`why-result-${r.school.split(" ")[0].toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <Trophy className="h-5 w-5" style={{ color: r.tone }} />
                  <span className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: r.tone }}>
                    Case study
                  </span>
                </div>
                <h3 className="mt-3 font-display font-black text-xl md:text-2xl text-white tracking-tight">
                  {r.school}
                </h3>
                <ul className="mt-3 space-y-1.5">
                  {r.body.map((line) => (
                    <li key={line} className="text-[13px] text-white/70 flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ backgroundColor: r.tone }} />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-2xl border p-6 md:p-8"
            style={{ backgroundColor: "rgba(15,30,54,0.55)", borderColor: "rgba(255,255,255,0.10)" }}
            data-testid="why-pattern"
          >
            <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">The pattern</div>
            <p className="mt-3 font-display font-black text-2xl md:text-3xl text-white tracking-tight leading-snug">
              Every successful program had one thing in common:<br />
              <span style={{ color: "var(--brand-secondary)" }}>they measured performance continuously and made objective decisions instead of emotional ones.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── THE BUILD ── */}
      <section className="py-20 md:py-28" data-testid="why-build">
        <div className="max-w-3xl mx-auto px-6 space-y-8">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">
            Why ArmIQ exists
          </div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            The digital <span style={{ color: "var(--brand-secondary)" }}>Athlete Performance Department</span> I always wished every program had.
          </h2>
          <Para>
            BioHacker Sports™ isn&apos;t another sport app. It isn&apos;t another radar gun. It isn&apos;t another biomechanics report.
          </Para>
          <Para>
            It&apos;s one platform that continuously monitors workload, biomechanics, recovery, readiness, performance, and health — transforming thousands of data points into one simple question every coach needs answered before the athlete competes:
          </Para>
          <p className="font-display font-black text-2xl md:text-3xl text-white tracking-tight leading-snug">
            <span style={{ color: "var(--brand-secondary)" }}>Is this athlete ready today?</span>
          </p>
          <Para>
            And if not — what needs to change? Warm-up. Session volume. Recovery. Practice plan. Training load.
          </Para>
          <Para>
            Everything becomes intentional.
          </Para>
        </div>
      </section>

      {/* ── BIGGER THAN BASEBALL ── */}
      <section className="py-20 md:py-28" style={{ backgroundColor: "var(--ink-0)" }} data-testid="why-bigger">
        <div className="max-w-3xl mx-auto px-6 space-y-8">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">
            Bigger than baseball
          </div>
          <Para>
            I didn&apos;t build ArmIQ because I wanted another piece of technology. I built it because I&apos;ve seen too many athletes get injured from guesswork. Too many coaches forced to make critical decisions without complete information. Too many programs operating on feel when data could provide clarity.
          </Para>
          <p className="font-display font-black text-3xl md:text-4xl text-white tracking-tight leading-tight">
            The future of player development isn&apos;t <span className="text-white/50">more technology.</span><br />
            It&apos;s <span style={{ color: "var(--brand-secondary)" }}>better decisions.</span>
          </p>
          <Para>
            ArmIQ exists to help coaches make those decisions with confidence — because you can&apos;t improve what you don&apos;t measure, and you can&apos;t protect what you don&apos;t continuously monitor.
          </Para>
        </div>
      </section>

      {/* ── FOUNDER BIO ── */}
      <section className="py-20 md:py-28" data-testid="why-founder">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">
            About the founder
          </div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter text-white leading-[1] mb-8">
            A. &quot;Rod&quot; White
          </h2>
          <div className="rounded-2xl border p-6 md:p-8 space-y-5 text-[15px] leading-relaxed text-white/75"
            style={{ backgroundColor: "rgba(15,30,54,0.55)", borderColor: "rgba(255,255,255,0.08)" }}
          >
            <p>
              Rod White has spent more than two decades viewing the game of baseball from virtually every perspective imaginable — as a <span className="text-white font-semibold">player, coach, scout, executive, general manager, certified sports agent, educator, and performance specialist</span>. That uncommon journey has shaped a philosophy centered on one belief: great athletes are built through <em>objective evaluation, intentional development, and informed decision-making</em>.
            </p>
            <p>
              A graduate of Scout School with the Toronto Blue Jays, Rod has served in scouting roles with the Boston Red Sox and Philadelphia Phillies organizations while evaluating talent from youth baseball to the professional ranks. He earned a Bachelor of Science in Physical Education from Alabama State University, where he was a collegiate baseball player and developed the educational foundation that continues to influence his approach to athlete development.
            </p>
            <p>
              Rod&apos;s professional career began in independent baseball with the Americus Arrows and then the Montgomery Wings, where he progressed through coaching, player development, baseball operations, and front office leadership before serving as Assistant General Manager and General Manager.
            </p>
            <p>
              A <span className="text-white font-semibold">Certified Biomechanics Specialist (BMS)</span>, <span className="text-white font-semibold">Certified High School Strength &amp; Conditioning Coach (CHSSC)</span>, and <span className="text-white font-semibold">Certified Sports Specific Trainer (CSSpT)</span>, Rod has become recognized for blending traditional baseball knowledge with biomechanics, movement science, strength and conditioning, performance analytics, and emerging technologies.
            </p>
            <p>
              His coaching career has included leadership and player development roles at the <span className="text-white font-semibold">professional, collegiate, summer collegiate, high school, and youth levels</span> — Montgomery Wings, Alabama State University, Carver High School, Texas College, ASA College Miami, the Medicine Hat Mavericks, and Dillard University.
            </p>
            <p>
              As the Founder and CEO of the <span className="text-white font-semibold">National Scouting Bureau (NSB)</span>, Rod has helped facilitate <span className="text-white font-semibold">more than 600 collegiate placements</span> and contributed to the identification and development of <span className="text-white font-semibold">more than 22 Major League Baseball Draft selections</span>.
            </p>
            <p>
              He is also the creator of <span className="text-white font-semibold">ArmIQ™</span>, the Athlete Performance Operating System that integrates biomechanics, movement intelligence, sports technology, and artificial intelligence into a unified platform for measuring and optimizing athletic performance.
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 0%, rgba(217,4,41,0.15), transparent 60%), linear-gradient(180deg, #05090F 0%, #0A1424 100%)",
        }}
      >
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            You can&apos;t grow what<br />
            you can&apos;t <span style={{ color: "var(--brand-secondary)" }}>measure.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/70">
            Ready to build your program on measurement instead of guesswork?
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup" data-testid="why-final-cta">
              <Button
                size="lg"
                className="rounded-full px-8 py-7 text-base text-white red-glow font-bold"
                style={{ backgroundColor: "var(--brand-secondary)" }}
              >
                Request access <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/high-school" data-testid="why-whitepaper-link">
              <Button size="lg" variant="outline" className="rounded-full px-8 py-7 text-base border-2 border-white/25 text-white bg-white/5 hover:bg-white/10">
                Get the whitepaper
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
